import { ImportFileTransferProvider } from './context/ImportFileTransferContext';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { onAuthStateChanged, multiFactor, signOut } from 'firebase/auth';
import {
  CircularProgress,
  Typography,
  createTheme,
  ThemeProvider,
  CssBaseline,
  Box,
  Alert,
  Button,
} from '@mui/material';
import { BrowserRouter } from 'react-router-dom';
import Login from './components/Login';
import MFAEnrollment from './components/MFAEnrollment';
import {
  isThemeDark,
  applyThemeVars,
  applyColorOverrides,
} from './styles/themeTokens';
import { buildAppThemeOptions } from './styles/themeOptions';
import ErrorBoundary from "./components/ErrorBoundary";
import {
  CUSTOMER_ACCESS_DENIED_EVENT,
  callBackend,
  isCustomerAccessDeniedError,
  isInvitationDenialError,
  isUserNotProvisionedError,
} from './utils/api';
import { useAppearancePreferencesSync } from './hooks/useAppearancePreferencesSync';
import AppRoutes from './AppRoutes';
import AppBarComponent from './components/AppBar';
import { resolveCurrentEnv } from './utils/environment';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  fetchCurrentWatchlistRevisionSignal,
  fetchSecurityResolutionCenter,
} from './utils/ApiCall';
import {
  WATCHLIST_REVISION_PROGRESS_EVENT,
  isSecurityOperationEventForScope,
  isSuccessfulWatchlistRevisionPublication,
  watchlistPublicationKey,
} from './utils/securityResolutionEvents';

import { AppContextProvider } from './context/AppContext';
import { SecurityOperationProvider } from './context/SecurityOperationContext';
import { auth } from './firebase';
import { performLogout } from './utils/auth';
import { useIdleLogout } from './hooks/useIdleLogout';
import IdleLogoutWarningDialog from './components/IdleLogoutWarningDialog';
import { mergeSecurityResolutionCenterResponse } from './utils/securityResolutionState';
import { cancelPendingBackendRequests } from './utils/backendRequestSession';
import useWatchlistPublicationCoordinator from './hooks/useWatchlistPublicationCoordinator';
import { clearUserWatchlistCache } from './hooks/useIndustries';
import InviteAcceptance from './components/InviteAcceptance';
import { buildAppMainSx } from './styles/appShellStyles';

const isPlainObject = (value) => (
  value != null &&
  typeof value === 'object' &&
  !Array.isArray(value)
);

const areJsonLikeValuesEqual = (left, right) => {
  if (Object.is(left, right)) {
    return true;
  }
  if (Array.isArray(left) && Array.isArray(right)) {
    return JSON.stringify(left) === JSON.stringify(right);
  }
  if (isPlainObject(left) && isPlainObject(right)) {
    return JSON.stringify(left) === JSON.stringify(right);
  }
  return false;
};

const AUTH_BOOTSTRAP_STATE = Object.freeze({
  RESOLVING_AUTH: 'RESOLVING_AUTH',
  CHECKING_MEMBERSHIP: 'CHECKING_MEMBERSHIP',
  AUTHENTICATED_PROVISIONED: 'AUTHENTICATED_PROVISIONED',
  AUTHENTICATED_MFA_REQUIRED: 'AUTHENTICATED_MFA_REQUIRED',
  ACCESS_DENIED: 'ACCESS_DENIED',
  TRANSIENT_FAILURE: 'TRANSIENT_FAILURE',
  INVITATION_ACCEPTANCE: 'INVITATION_ACCEPTANCE',
  SIGNED_OUT: 'SIGNED_OUT',
});

const isInvitationAcceptancePath = () => (
  typeof window !== 'undefined' && window.location.pathname === '/accept-invite'
);

const PERFORMANCE_SESSION_RESET_KEYS = [
  'perf.intraday.watchlist_nudge_opt_out',
  'perf.historical.watchlist_nudge_opt_out',
];

const sleep = (delayMs) => new Promise((resolve) => {
  window.setTimeout(resolve, delayMs);
});

const securityResolutionRevisionId = (response) => String(
  response?.watchlist_field_contract?.expected_watchlist_revision_id || '',
).trim();

const resetPerformanceSessionFlags = () => {
  if (typeof window === 'undefined') {
    return;
  }
  for (const key of PERFORMANCE_SESSION_RESET_KEYS) {
    sessionStorage.removeItem(key);
  }
};

function App() {
  const [navigationCollapsed, setNavigationCollapsed] = useState(false);
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [bootstrapState, setBootstrapState] = useState(AUTH_BOOTSTRAP_STATE.RESOLVING_AUTH);
  const [bootstrapRetrySequence, setBootstrapRetrySequence] = useState(0);
  const [accessDeniedMessage, setAccessDeniedMessage] = useState('');
  const [accessDeniedSignOutFailed, setAccessDeniedSignOutFailed] = useState(false);
  const [hasMfaEnrolled, setHasMfaEnrolled] = useState(false);
  const authGenerationRef = useRef(0);
  const customerAccessSignOutStartedRef = useRef(false);
  const previousUserUidRef = useRef('');
  const securityOperationSessionSequenceRef = useRef(0);
  const [securityOperationSessionScopeToken, setSecurityOperationSessionScopeToken] = useState('');
  const [canAdminEnvs, setCanAdminEnvs] = useState({});
  const currentEnv = resolveCurrentEnv();
  const [msiResolutionCenter, setMsiResolutionCenter] = useState({
    loading: false,
    error: '',
    data: null,
  });
  const msiResolutionCenterStatusRef = useRef({
    loading: false,
    loaded: false,
    hasData: false,
  });
  const msiResolutionCenterRefreshQueuedRef = useRef(false);
  const msiResolutionPublicationRefreshKeysRef = useRef(new Set());
  const msiResolutionPublicationWatermarkRef = useRef({
    revisionSeq: 0,
    revisionId: '',
  });
  const msiResolutionExpectedRevisionRef = useRef('');
  const msiResolutionCenterRequestGenerationRef = useRef(0);
  const msiResolutionCenterUserUidRef = useRef('');
  const msiResolutionCenterSessionScopeRef = useRef('');
  const isApplicationAuthorized = Boolean(
    user
    && userData
    && hasMfaEnrolled
    && bootstrapState === AUTH_BOOTSTRAP_STATE.AUTHENTICATED_PROVISIONED
  );
  const authorizedUser = isApplicationAuthorized ? user : null;
  const {
    statusDoc: securityOperationStatus,
    readState: securityOperationReadState,
    readError: securityOperationReadError,
    publicationState: watchlistPublicationState,
    retryClientPublication: retryWatchlistClientPublication,
    pendingReveal: pendingWatchlistReveal,
    acknowledgeReveal: acknowledgeWatchlistReveal,
    commitBootstrapRevision: commitWatchlistBootstrapRevision,
    registerPublisher: registerWatchlistPublicationExecutor,
    exclusiveCoreLoadOwnerRef,
  } = useWatchlistPublicationCoordinator({
    userUid: authorizedUser?.uid,
    sessionScopeToken: securityOperationSessionScopeToken,
    // Own the watchlist parent-operation feed at the authenticated app shell.
    // Watchlist and Security Resolution can then render one shared monotonic
    // status even when navigation occurs after the operation already started.
    enabled: Boolean(authorizedUser?.uid),
    fetchAcceptedRevisionSignal: fetchCurrentWatchlistRevisionSignal,
  });

  const {
    themeId, setThemeId,
    compactMode, toggleCompactMode,
    colorOverrides, setColorOverride, resetColorOverride, resetAllColorOverrides,
    idleLogoutMinutes, setIdleLogoutMinutes,
    newsRecencyHours, setNewsRecencyHours,
    sectorPaletteOverride, setSectorPaletteOverride,
    deepDiveFocusAreas, setDeepDiveFocusAreas,
    maxSharpeSkill, setMaxSharpeSkill,
    compareExploreColumns, setCompareExploreColumns,
  } = useAppearancePreferencesSync({ userData });

  // Derived so all existing darkMode consumers (MUI paper, AppRoutes) keep working.
  const darkMode = isThemeDark(themeId);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const handleCustomerAccessDenied = (event) => {
      if (
        bootstrapState !== AUTH_BOOTSTRAP_STATE.AUTHENTICATED_PROVISIONED
        || customerAccessSignOutStartedRef.current
      ) {
        return;
      }
      customerAccessSignOutStartedRef.current = true;
      const deniedUid = String(user?.uid || '').trim();
      setUserData(null);
      setHasMfaEnrolled(false);
      setCanAdminEnvs({});
      cancelPendingBackendRequests();
      if (deniedUid) {
        clearUserWatchlistCache(deniedUid);
      }
      setAccessDeniedMessage(
        event?.detail?.message
        || 'This Alphamizer account is not currently active. Contact support for assistance.',
      );
      setBootstrapState(AUTH_BOOTSTRAP_STATE.ACCESS_DENIED);
      void signOut(auth).catch((error) => {
        console.error('Unable to complete invalid-access sign-out:', error);
        setAccessDeniedSignOutFailed(true);
      });
    };
    window.addEventListener(CUSTOMER_ACCESS_DENIED_EVENT, handleCustomerAccessDenied);
    return () => window.removeEventListener(
      CUSTOMER_ACCESS_DENIED_EVENT,
      handleCustomerAccessDenied,
    );
  }, [bootstrapState, user?.uid]);

  useLayoutEffect(() => {
    // applyThemeVars resets every var to the theme default, so overrides must
    // be reapplied right after on theme change.
    applyThemeVars(themeId);
    applyColorOverrides(colorOverrides);
    // Compact mode for Tailwind components (the table kit's `[[data-compact]_&]`
    // variants); MUI components read it from the theme.
    document.documentElement.toggleAttribute('data-compact', Boolean(compactMode));
  }, [themeId, colorOverrides, compactMode]);

  const handleIdleLogout = useCallback(() => {
    void performLogout(authorizedUser);
  }, [authorizedUser]);

  const { secondsRemaining: idleLogoutSecondsRemaining, stayLoggedIn: handleStayLoggedIn } = useIdleLogout({
    timeoutMinutes: idleLogoutMinutes,
    enabled: Boolean(authorizedUser),
    onLogout: handleIdleLogout,
  });

  const theme = createTheme(buildAppThemeOptions(themeId, compactMode));

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      authGenerationRef.current += 1;
      const authGeneration = authGenerationRef.current;
      const nextUserUid = String(currentUser?.uid || '').trim();
      const previousUserUid = previousUserUidRef.current;
      previousUserUidRef.current = nextUserUid;

      cancelPendingBackendRequests();
      resetPerformanceSessionFlags();
      setUserData(null);
      setHasMfaEnrolled(false);
      setCanAdminEnvs({});
      setAccessDeniedSignOutFailed(false);
      if (previousUserUid && previousUserUid !== nextUserUid) {
        clearUserWatchlistCache(previousUserUid);
      }

      securityOperationSessionSequenceRef.current += 1;
      const nextSessionScopeToken = nextUserUid
        ? `security-operation-session:${securityOperationSessionSequenceRef.current}:${nextUserUid}`
        : '';
      setSecurityOperationSessionScopeToken(nextSessionScopeToken);
      msiResolutionCenterUserUidRef.current = nextUserUid;
      msiResolutionCenterSessionScopeRef.current = nextSessionScopeToken;
      msiResolutionCenterRequestGenerationRef.current += 1;
      msiResolutionCenterRefreshQueuedRef.current = false;
      msiResolutionExpectedRevisionRef.current = '';
      msiResolutionPublicationWatermarkRef.current = {
        revisionSeq: 0,
        revisionId: '',
      };
      msiResolutionPublicationRefreshKeysRef.current.clear();
      setUser(currentUser);

      if (currentUser) {
        customerAccessSignOutStartedRef.current = false;
        setAccessDeniedMessage('');
        setBootstrapState(
          isInvitationAcceptancePath()
            ? AUTH_BOOTSTRAP_STATE.INVITATION_ACCEPTANCE
            : AUTH_BOOTSTRAP_STATE.CHECKING_MEMBERSHIP,
        );
        const mfaInstance = multiFactor(currentUser);
        const isMfaEnrolled = mfaInstance.enrolledFactors?.length > 0;
        setHasMfaEnrolled(isMfaEnrolled);

        // can_admin_envs is a custom claim -- only current as of this token's
        // mint time (next refresh / re-login), same staleness as ADMIN_ACCESS.md
        // documents for the backend's own claim reads.
        try {
          const tokenResult = await currentUser.getIdTokenResult();
          if (
            authGenerationRef.current === authGeneration
            && previousUserUidRef.current === nextUserUid
          ) {
            setCanAdminEnvs(tokenResult.claims?.can_admin_envs || {});
          }
        } catch (error) {
          if (authGenerationRef.current === authGeneration) {
            console.error('Error reading can_admin_envs claim:', error);
            setCanAdminEnvs({});
          }
        }
      } else {
        setBootstrapState(
          isInvitationAcceptancePath()
            ? AUTH_BOOTSTRAP_STATE.INVITATION_ACCEPTANCE
            : AUTH_BOOTSTRAP_STATE.SIGNED_OUT,
        );
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const activeUserUid = String(user?.uid || '').trim();
    msiResolutionCenterUserUidRef.current = activeUserUid;
    msiResolutionCenterSessionScopeRef.current = securityOperationSessionScopeToken;
    setMsiResolutionCenter({
      loading: false,
      error: '',
      data: null,
    });
    msiResolutionCenterStatusRef.current = {
      loading: false,
      loaded: false,
      hasData: false,
    };
    msiResolutionCenterRefreshQueuedRef.current = false;
    msiResolutionExpectedRevisionRef.current = '';
    msiResolutionPublicationWatermarkRef.current = {
      revisionSeq: 0,
      revisionId: '',
    };
    msiResolutionPublicationRefreshKeysRef.current.clear();
  }, [securityOperationSessionScopeToken, user?.uid]);

  useEffect(() => {
    let cancelled = false;
    const bootstrapUid = String(user?.uid || '').trim();
    const bootstrapAuthGeneration = authGenerationRef.current;
    const isCurrentAuthSession = () => (
      !cancelled
      && authGenerationRef.current === bootstrapAuthGeneration
      && previousUserUidRef.current === bootstrapUid
    );

    const bootstrapUserSession = async () => {
      if (!user) {
        setUserData(null);
        return;
      }
      if (isInvitationAcceptancePath()) {
        setBootstrapState(AUTH_BOOTSTRAP_STATE.INVITATION_ACCEPTANCE);
        return;
      }

      setBootstrapState(AUTH_BOOTSTRAP_STATE.CHECKING_MEMBERSHIP);
      const applyProvisionedMembership = (response) => {
        // Reaching this point means the backend accepted a token minted after
        // the required phone second-factor challenge.
        setHasMfaEnrolled(true);
        setUserData(response.userData);
        setBootstrapState(AUTH_BOOTSTRAP_STATE.AUTHENTICATED_PROVISIONED);
      };
      try {
        const bootstrapResponse = await callBackend('api/auth/bootstrap', 'POST', {});
        if (!isCurrentAuthSession()) {
          return;
        }
        const membership = bootstrapResponse?.membership;
        if (membership !== 'provisioned' && membership !== 'invited') {
          throw Object.assign(new Error('No active membership or invitation.'), {
            status: 404,
            code: 'INVITATION_NOT_AVAILABLE',
          });
        }
        if (
          bootstrapResponse?.access_mode === 'enforced'
          && bootstrapResponse?.access?.valid === false
        ) {
          throw Object.assign(new Error('Customer access is not valid.'), {
            status: 403,
            code: bootstrapResponse.access.code,
            customerAccessMessage: bootstrapResponse.access.message,
          });
        }

        const isMfaEnrolled = multiFactor(user).enrolledFactors?.length > 0;
        setHasMfaEnrolled(isMfaEnrolled);
        if (!isMfaEnrolled) {
          setBootstrapState(AUTH_BOOTSTRAP_STATE.AUTHENTICATED_MFA_REQUIRED);
          return;
        }

        if (membership === 'invited') {
          await callBackend('api/auth/invitations/accept', 'POST', {});
        }
        const response = await callBackend(
          'fetch_user_data',
          'POST',
          { uid: bootstrapUid },
        );
        if (isCurrentAuthSession()) {
          applyProvisionedMembership(response);
        }
      } catch (error) {
        if (!isCurrentAuthSession()) {
          return;
        }

        setUserData(null);
        cancelPendingBackendRequests();
        if (isCustomerAccessDeniedError(error) || error?.customerAccessMessage) {
          clearUserWatchlistCache(bootstrapUid);
          setAccessDeniedMessage(
            error?.customerAccessMessage
            || 'This Alphamizer account is not currently active. Contact support for assistance.',
          );
          setBootstrapState(AUTH_BOOTSTRAP_STATE.ACCESS_DENIED);
          try {
            customerAccessSignOutStartedRef.current = true;
            await signOut(auth);
          } catch (signOutError) {
            console.error('Unable to complete invalid-access sign-out:', signOutError);
            if (isCurrentAuthSession()) {
              setAccessDeniedSignOutFailed(true);
            }
          }
        } else if (isInvitationDenialError(error) || isUserNotProvisionedError(error)) {
          clearUserWatchlistCache(bootstrapUid);
          setAccessDeniedMessage('This account has not been invited to Alphamizer. Contact an administrator.');
          setBootstrapState(AUTH_BOOTSTRAP_STATE.ACCESS_DENIED);
          try {
            await signOut(auth);
          } catch (signOutError) {
            console.error('Unable to complete denied-user sign-out:', signOutError);
            if (isCurrentAuthSession()) {
              setAccessDeniedSignOutFailed(true);
            }
          }
        } else if (error?.code === 'MFA_REQUIRED') {
          clearUserWatchlistCache(bootstrapUid);
          setAccessDeniedMessage('Sign in again and complete SMS multi-factor authentication.');
          setBootstrapState(AUTH_BOOTSTRAP_STATE.ACCESS_DENIED);
          try {
            await signOut(auth);
          } catch (signOutError) {
            console.error('Unable to sign out a first-factor-only session:', signOutError);
            if (isCurrentAuthSession()) {
              setAccessDeniedSignOutFailed(true);
            }
          }
        } else {
          console.error('Error bootstrapping user session:', error);
          setBootstrapState(AUTH_BOOTSTRAP_STATE.TRANSIENT_FAILURE);
        }
      }
    };

    bootstrapUserSession();

    return () => {
      cancelled = true;
    };
  }, [bootstrapRetrySequence, user]);

  const ensureMsiResolutionCenterLoaded = useCallback(async (options = {}) => {
    const force = Boolean(options?.force);
    let background = Boolean(options?.background);
    if (!authorizedUser) {
      return;
    }
    if (msiResolutionCenterStatusRef.current.loading) {
      if (force) {
        msiResolutionCenterRefreshQueuedRef.current = true;
      }
      return;
    }
    if (msiResolutionCenterStatusRef.current.loaded && !force) {
      return;
    }
    const requestGeneration = msiResolutionCenterRequestGenerationRef.current;
    const requestUserUid = String(authorizedUser.uid || '').trim();
    const requestSessionScopeToken = securityOperationSessionScopeToken;
    const isCurrentRequest = () => (
      msiResolutionCenterRequestGenerationRef.current === requestGeneration
      && msiResolutionCenterUserUidRef.current === requestUserUid
      && msiResolutionCenterSessionScopeRef.current === requestSessionScopeToken
    );

    let publicationReadAttempt = 0;
    do {
      const backgroundForAttempt = background;
      msiResolutionCenterRefreshQueuedRef.current = false;
      msiResolutionCenterStatusRef.current.loading = true;
      setMsiResolutionCenter((current) => ({
        ...current,
        loading: backgroundForAttempt ? current.loading : true,
        error: '',
      }));

      try {
        const response = await fetchSecurityResolutionCenter();
        if (!isCurrentRequest()) {
          return;
        }
        const expectedRevisionId = msiResolutionExpectedRevisionRef.current;
        const observedRevisionId = securityResolutionRevisionId(response);
        const publicationStillInvisible = Boolean(
          expectedRevisionId
          && observedRevisionId !== expectedRevisionId
        );

        if (response?.success === false) {
          console.error('Failed to fetch Security Resolution center:', response.message);
          setMsiResolutionCenter((current) => ({
            loading: false,
            error: response?.message || 'Failed to load security resolution data.',
            data: backgroundForAttempt ? current.data : null,
          }));
        } else if (publicationStillInvisible) {
          // A terminal browser signal can win the race with read-model
          // visibility by a few milliseconds. Never replace the coherent
          // table with the preceding revision; retry the publication fence.
          publicationReadAttempt += 1;
          msiResolutionCenterRefreshQueuedRef.current = publicationReadAttempt < 5;
          msiResolutionCenterStatusRef.current.loaded = false;
        } else {
          setMsiResolutionCenter((current) => (
            mergeSecurityResolutionCenterResponse(current, response)
          ));
          msiResolutionCenterStatusRef.current.loaded = true;
          msiResolutionCenterStatusRef.current.hasData = true;
          if (!expectedRevisionId || observedRevisionId === expectedRevisionId) {
            msiResolutionExpectedRevisionRef.current = '';
          }
        }
      } catch (error) {
        if (!isCurrentRequest()) {
          return;
        }
        console.error('Unexpected error while fetching Security Resolution center:', error);
        setMsiResolutionCenter((current) => ({
          loading: false,
          error: error instanceof Error ? error.message : 'Failed to load security resolution data.',
          data: backgroundForAttempt ? current.data : null,
        }));
      } finally {
        if (isCurrentRequest()) {
          msiResolutionCenterStatusRef.current.loading = false;
        }
      }

      // If a parent operation published while this read was in flight, issue
      // one follow-up read against the newly published revision. Keep the last
      // coherent rows visible during that follow-up.
      background = true;
      if (msiResolutionCenterRefreshQueuedRef.current && publicationReadAttempt > 0) {
        await sleep(Math.min(1000, publicationReadAttempt * 200));
      }
    } while (isCurrentRequest() && msiResolutionCenterRefreshQueuedRef.current);
  }, [
    authorizedUser,
    securityOperationSessionScopeToken,
  ]);

  const refreshMsiResolutionCenter = useCallback(async (options = {}) => {
    const wasLoaded = msiResolutionCenterStatusRef.current.loaded;
    msiResolutionCenterStatusRef.current.loaded = false;
    await ensureMsiResolutionCenterLoaded({
      force: true,
      background: options?.background ?? wasLoaded,
    });
  }, [ensureMsiResolutionCenterLoaded]);

  const refreshPublishedMsiResolutionCenter = useCallback((publication) => {
    const refreshKey = watchlistPublicationKey(publication);
    const expectedRevisionId = String(
      publication?.watchlist_revision_id
      || '',
    ).trim();
    const revisionSeq = Number(publication?.revision_seq);
    const watermark = msiResolutionPublicationWatermarkRef.current;
    if (
      Number.isSafeInteger(revisionSeq)
      && revisionSeq > 0
      && (
        revisionSeq < watermark.revisionSeq
        || (
          revisionSeq === watermark.revisionSeq
          && watermark.revisionId
          && expectedRevisionId !== watermark.revisionId
        )
      )
    ) {
      return;
    }
    if (
      refreshKey
      && msiResolutionPublicationRefreshKeysRef.current.has(refreshKey)
    ) {
      return;
    }
    if (Number.isSafeInteger(revisionSeq) && revisionSeq > 0 && expectedRevisionId) {
      msiResolutionPublicationWatermarkRef.current = {
        revisionSeq,
        revisionId: expectedRevisionId,
      };
    }
    if (refreshKey) {
      msiResolutionPublicationRefreshKeysRef.current.add(refreshKey);
      if (msiResolutionPublicationRefreshKeysRef.current.size > 64) {
        const oldestKey = msiResolutionPublicationRefreshKeysRef.current.values().next().value;
        msiResolutionPublicationRefreshKeysRef.current.delete(oldestKey);
      }
    }

    const hadPublishedReadModel = (
      msiResolutionCenterStatusRef.current.loaded
      || msiResolutionCenterStatusRef.current.loading
      || msiResolutionCenterStatusRef.current.hasData
    );
    if (!hadPublishedReadModel) {
      return;
    }
    if (expectedRevisionId) {
      msiResolutionExpectedRevisionRef.current = expectedRevisionId;
    }
    msiResolutionCenterStatusRef.current.loaded = false;
    void ensureMsiResolutionCenterLoaded({ force: true, background: true });
  }, [ensureMsiResolutionCenterLoaded]);

  useEffect(() => {
    // The app shell owns cache invalidation and exactly-once replacement. The
    // table remains frozen for nonterminal progress; a successful atomic
    // publication replaces a previously loaded read model in the background.
    const handleWatchlistProgress = (event) => {
      if (!isSecurityOperationEventForScope(
        event?.detail,
        securityOperationSessionScopeToken,
      )) return;
      if (isSuccessfulWatchlistRevisionPublication(event?.detail)) {
        refreshPublishedMsiResolutionCenter(event.detail);
      }
    };
    window.addEventListener(WATCHLIST_REVISION_PROGRESS_EVENT, handleWatchlistProgress);
    return () => {
      window.removeEventListener(WATCHLIST_REVISION_PROGRESS_EVENT, handleWatchlistProgress);
    };
  }, [refreshPublishedMsiResolutionCenter, securityOperationSessionScopeToken]);

  const handleUserDataChange = useCallback((updates) => {
    setUserData((current) => {
      if (!current) {
        return current;
      }
      if (
        !updates ||
        Object.keys(updates).every((key) => areJsonLikeValuesEqual(current[key], updates[key]))
      ) {
        return current;
      }
      return {
        ...current,
        ...updates,
      };
    });
  }, []);

  const handleAuthenticationAttempt = useCallback(() => {
    setAccessDeniedMessage('');
    setAccessDeniedSignOutFailed(false);
  }, []);

  const handleBootstrapRetry = useCallback(() => {
    setBootstrapRetrySequence((current) => current + 1);
  }, []);

  const handleInvitationAccepted = useCallback(() => {
    window.history.replaceState({}, '', '/dashboard/overview');
    setAccessDeniedMessage('');
    setBootstrapState(AUTH_BOOTSTRAP_STATE.CHECKING_MEMBERSHIP);
    setBootstrapRetrySequence((current) => current + 1);
  }, []);

  const handleDeniedSignOutRetry = useCallback(async () => {
    setAccessDeniedSignOutFailed(false);
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Unable to complete denied-user sign-out:', error);
      setAccessDeniedSignOutFailed(true);
    }
  }, []);

  if (
    bootstrapState === AUTH_BOOTSTRAP_STATE.RESOLVING_AUTH
    || bootstrapState === AUTH_BOOTSTRAP_STATE.CHECKING_MEMBERSHIP
  ) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            flexDirection: 'column',
          }}
        >
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>Loading...</Typography>
        </Box>
      </ThemeProvider>
    );
  }
  return (
    <SecurityOperationProvider
      status={securityOperationStatus}
      publicationState={watchlistPublicationState}
      retryClientPublication={retryWatchlistClientPublication}
      pendingReveal={pendingWatchlistReveal}
      acknowledgeReveal={acknowledgeWatchlistReveal}
      readState={securityOperationReadState}
      readError={securityOperationReadError}
      sessionScopeToken={securityOperationSessionScopeToken}
    >
    <AppContextProvider
      sectorPaletteOverride={sectorPaletteOverride}
      setSectorPaletteOverride={setSectorPaletteOverride}
    >
    <ThemeProvider theme={theme}>
      <CssBaseline />
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <ErrorBoundary>
            <BrowserRouter>
              <ImportFileTransferProvider key={securityOperationSessionScopeToken}>
              {bootstrapState === AUTH_BOOTSTRAP_STATE.AUTHENTICATED_PROVISIONED ? (
                  <>
                    <AppBarComponent
                      navigationCollapsed={navigationCollapsed}
                      onNavigationCollapsedChange={setNavigationCollapsed}
                      user={user}
                      userData={userData}
                      onUserDataChange={handleUserDataChange}
                      isAdmin={canAdminEnvs?.[currentEnv] === true}
                      currentEnv={currentEnv}
                      compactMode={compactMode}
                      toggleCompactMode={toggleCompactMode}
                      msiResolutionCenter={msiResolutionCenter}
                      onEnsureMsiResolutionCenterLoaded={ensureMsiResolutionCenterLoaded}
                      onRefreshMsiResolutionCenter={refreshMsiResolutionCenter}
                      securityOperationStatus={securityOperationStatus}
                      watchlistPublicationState={watchlistPublicationState}
                      themeId={themeId}
                      setThemeId={setThemeId}
                      colorOverrides={colorOverrides}
                      setColorOverride={setColorOverride}
                      resetColorOverride={resetColorOverride}
                      resetAllColorOverrides={resetAllColorOverrides}
                      idleLogoutMinutes={idleLogoutMinutes}
                      setIdleLogoutMinutes={setIdleLogoutMinutes}
                      newsRecencyHours={newsRecencyHours}
                      setNewsRecencyHours={setNewsRecencyHours}
                      deepDiveFocusAreas={deepDiveFocusAreas}
                      setDeepDiveFocusAreas={setDeepDiveFocusAreas}
                    />
                    <IdleLogoutWarningDialog
                      secondsRemaining={idleLogoutSecondsRemaining}
                      onStayLoggedIn={handleStayLoggedIn}
                    />
                    <Box component="main" sx={buildAppMainSx(navigationCollapsed)}>
                      <AppRoutes
                        user={user}
                        isAdmin={canAdminEnvs?.[currentEnv] === true}
                        currentEnv={currentEnv}
                        userData={userData}
                        onUserDataChange={handleUserDataChange}
                        darkMode={darkMode}
                        compactMode={compactMode}
                        newsRecencyHours={newsRecencyHours}
                        deepDiveFocusAreas={deepDiveFocusAreas}
                        maxSharpeSkill={maxSharpeSkill}
                        setMaxSharpeSkill={setMaxSharpeSkill}
                        compareExploreColumns={compareExploreColumns}
                        setCompareExploreColumns={setCompareExploreColumns}
                        securityOperationStatus={securityOperationStatus}
                        watchlistPublicationState={watchlistPublicationState}
                        onRegisterWatchlistPublicationExecutor={registerWatchlistPublicationExecutor}
                        onCommitWatchlistBootstrapRevision={commitWatchlistBootstrapRevision}
                        securityOperationSessionScopeToken={securityOperationSessionScopeToken}
                        exclusiveCoreLoadOwnerRef={exclusiveCoreLoadOwnerRef}
                      />
                    </Box>
                  </>
              ) : bootstrapState === AUTH_BOOTSTRAP_STATE.INVITATION_ACCEPTANCE ? (
                <InviteAcceptance user={user} onAccepted={handleInvitationAccepted} />
              ) : bootstrapState === AUTH_BOOTSTRAP_STATE.AUTHENTICATED_MFA_REQUIRED ? (
                <MFAEnrollment />
              ) : bootstrapState === AUTH_BOOTSTRAP_STATE.ACCESS_DENIED ? (
                <Box
                  sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 3,
                  }}
                >
                  <Alert
                    severity="error"
                    action={accessDeniedSignOutFailed ? (
                      <Button color="inherit" size="small" onClick={handleDeniedSignOutRetry}>
                        Retry sign-out
                      </Button>
                    ) : null}
                  >
                    {accessDeniedMessage || 'Access to Alphamizer is not provisioned for this account.'}
                    {!accessDeniedSignOutFailed && ' Signing out…'}
                  </Alert>
                </Box>
              ) : bootstrapState === AUTH_BOOTSTRAP_STATE.TRANSIENT_FAILURE ? (
                <Box
                  sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 3,
                  }}
                >
                  <Alert
                    severity="warning"
                    action={(
                      <Button color="inherit" size="small" onClick={handleBootstrapRetry}>
                        Retry
                      </Button>
                    )}
                  >
                    Alphamizer could not verify your application access. No application data has been loaded.
                  </Alert>
                </Box>
              ) : (
                <Login
                  initialError={accessDeniedMessage}
                  onAuthAttempt={handleAuthenticationAttempt}
                />
              )}
                          </ImportFileTransferProvider>
            </BrowserRouter>
          </ErrorBoundary>
        </LocalizationProvider>
    </ThemeProvider>
    </AppContextProvider>
    </SecurityOperationProvider>
  );
}

export default App;
